// useAnchorRect — track an anchor element's bounding box and re-emit it on
// resize, scroll, and a low-frequency interval (covers cases where layout
// shifts due to React state updates without a corresponding scroll/resize).
//
// Returns `null` if the anchor selector matches nothing — callers render an
// unattached popover in that case.

import { useEffect, useState } from 'react';
import type { TourRect } from './types';

const POLL_INTERVAL_MS = 250;

function resolveSelector(anchor: string): string {
  return anchor.startsWith('[') ? anchor : `[data-tour="${anchor}"]`;
}

function readRect(anchor: string | null): TourRect | null {
  if (!anchor) return null;
  const el = document.querySelector(resolveSelector(anchor));
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    left: r.left,
    top: r.top,
    right: r.right,
    bottom: r.bottom,
    width: r.width,
    height: r.height,
  };
}

export default function useAnchorRect(anchor: string | null): TourRect | null {
  const [rect, setRect] = useState<TourRect | null>(() => readRect(anchor));

  useEffect(() => {
    if (!anchor) {
      setRect(null);
      return;
    }

    let prev: TourRect | null = null;

    const update = () => {
      const next = readRect(anchor);
      // Skip set when the rect is unchanged so React doesn't re-render the
      // popover 4× a second.
      if (
        next &&
        prev &&
        next.left === prev.left &&
        next.top === prev.top &&
        next.width === prev.width &&
        next.height === prev.height
      ) {
        return;
      }
      prev = next;
      setRect(next);
    };

    update();
    window.addEventListener('resize', update);
    // Capture-phase scroll listener so we catch scrolls inside any nested
    // overflow containers (the audience builder has several).
    window.addEventListener('scroll', update, true);
    const id = window.setInterval(update, POLL_INTERVAL_MS);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.clearInterval(id);
    };
  }, [anchor]);

  return rect;
}
